/**
 * Soft Clarity Half-Dark Theme
 *
 * Inspired by Asana/Todoist/Height with a "dim" dark mode.
 * Not pitch black — warm charcoal with indigo accents.
 */

import type { AppTheme } from './types';

const palette = {
  bg: {
    base: '#2a2c42',
    surface: '#32344d',
    elevated: '#3a3c57',
    hover: '#434660',
    active: '#4c4f6a',
  },
  text: {
    primary: '#eef0f8',
    secondary: '#a8aac0',
    tertiary: '#7d8096',
    inverse: '#2a2c42',
  },
  border: {
    default: '#434660',
    subtle: '#3a3c57',
    strong: '#545778',
  },
  primary: '#6366f1',
  primaryHover: '#818cf8',
  primaryActive: '#4f46e5',
  primarySubtle: 'rgba(99, 102, 241, 0.12)',
  success: '#22c55e',
  successSubtle: 'rgba(34, 197, 94, 0.12)',
  warning: '#f59e0b',
  warningSubtle: 'rgba(245, 158, 11, 0.12)',
  error: '#ef4444',
  errorSubtle: 'rgba(239, 68, 68, 0.12)',
  info: '#3b82f6',
  infoSubtle: 'rgba(59, 130, 246, 0.12)',
};

export const clarityTheme: AppTheme = {
  name: 'clarity',
  displayName: 'Soft Clarity',
  description: 'Half-dark mode with indigo accents — warm charcoal, not pitch black',
  isDark: true,

  antdTheme: {
    token: {
      colorPrimary: palette.primary,
      colorPrimaryHover: palette.primaryHover,
      colorPrimaryActive: palette.primaryActive,
      colorPrimaryBg: palette.primarySubtle,

      colorSuccess: palette.success,
      colorSuccessBg: palette.successSubtle,
      colorWarning: palette.warning,
      colorWarningBg: palette.warningSubtle,
      colorError: palette.error,
      colorErrorBg: palette.errorSubtle,
      colorInfo: palette.info,
      colorInfoBg: palette.infoSubtle,

      colorLink: palette.primaryHover,
      colorLinkHover: '#a5b4fc',
      colorLinkActive: palette.primary,

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 14,
      paddingLG: 20,
      paddingXL: 28,
      margin: 14,
      marginLG: 20,
      marginXL: 28,

      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      colorBgContainer: palette.bg.surface,
      colorBgElevated: palette.bg.elevated,
      colorBgLayout: palette.bg.base,
      colorBgSpotlight: palette.bg.hover,
      colorBgMask: 'rgba(0, 0, 0, 0.65)',
      colorFillContent: palette.bg.hover,
      colorFillContentHover: palette.bg.active,
      colorFillAlter: palette.bg.hover,
      colorFill: palette.bg.active,
      colorFillSecondary: palette.bg.hover,
      colorFillTertiary: palette.bg.surface,
      colorFillQuaternary: palette.bg.base,

      colorBorder: palette.border.default,
      colorBorderSecondary: palette.border.subtle,

      colorText: palette.text.primary,
      colorTextSecondary: palette.text.secondary,
      colorTextTertiary: palette.text.tertiary,
      colorTextQuaternary: palette.text.tertiary,
      colorTextDescription: palette.text.secondary,
      colorTextDisabled: palette.text.tertiary,
      colorTextPlaceholder: palette.text.tertiary,

      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
      boxShadowSecondary: '0 2px 8px rgba(0, 0, 0, 0.2)',

      colorIcon: palette.text.secondary,
      colorIconHover: palette.text.primary,

      controlItemBgHover: palette.bg.hover,
      controlItemBgActive: palette.primarySubtle,
      controlItemBgActiveHover: 'rgba(99, 102, 241, 0.18)',
      controlOutline: 'rgba(99, 102, 241, 0.15)',
    },

    components: {
      Layout: {
        headerBg: palette.bg.surface,
        headerColor: palette.text.primary,
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: palette.bg.base,
        bodyBg: palette.bg.base,
      },

      Menu: {
        darkItemBg: 'transparent',
        darkItemColor: palette.text.secondary,
        darkItemHoverBg: palette.bg.hover,
        darkItemHoverColor: palette.text.primary,
        darkItemSelectedBg: palette.primarySubtle,
        darkItemSelectedColor: palette.primaryHover,
        darkSubMenuItemBg: palette.bg.base,
        itemBg: 'transparent',
        itemColor: palette.text.secondary,
        itemHoverBg: palette.bg.hover,
        itemHoverColor: palette.text.primary,
        itemSelectedBg: palette.primarySubtle,
        itemSelectedColor: palette.primaryHover,
        subMenuItemBg: palette.bg.surface,
        horizontalItemSelectedBg: 'transparent',
        horizontalItemSelectedColor: palette.primary,
        horizontalItemHoverBg: 'transparent',
        horizontalItemHoverColor: palette.text.primary,
      },

      Button: {
        borderRadius: 8,
        borderRadiusLG: 10,
        borderRadiusSM: 6,
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 28,
        fontWeight: 600,
        primaryShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
        defaultBg: palette.bg.elevated,
        defaultColor: palette.text.primary,
        defaultBorderColor: palette.border.strong,
        defaultGhostColor: palette.text.primary,
        defaultGhostBorderColor: palette.border.strong,
      },

      Card: {
        colorBgContainer: palette.bg.surface,
        borderRadius: 12,
        borderRadiusLG: 14,
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
        headerBg: palette.bg.surface,
      },

      Table: {
        headerBg: palette.bg.hover,
        headerColor: palette.text.secondary,
        headerBorderRadius: 8,
        borderRadius: 8,
        rowHoverBg: palette.bg.hover,
        colorBgContainer: palette.bg.surface,
        cellPaddingBlock: 12,
        cellPaddingInline: 14,
        borderColor: palette.border.subtle,
        headerSplitColor: palette.border.subtle,
      },

      Input: {
        colorBgContainer: palette.bg.elevated,
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 44,
        paddingBlock: 8,
        paddingInline: 14,
        activeBorderColor: palette.primary,
        hoverBorderColor: palette.border.strong,
        activeShadow: '0 0 0 2px rgba(99, 102, 241, 0.15)',
      },

      Select: {
        colorBgContainer: palette.bg.elevated,
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 44,
        optionSelectedBg: palette.primarySubtle,
        optionActiveBg: palette.bg.hover,
        selectorBg: palette.bg.elevated,
      },

      Tag: {
        borderRadiusSM: 6,
        fontSizeSM: 12,
        defaultBg: palette.bg.hover,
        defaultColor: palette.text.secondary,
      },

      Badge: {
        fontSize: 12,
        colorBgContainer: palette.bg.surface,
      },

      Modal: {
        contentBg: palette.bg.surface,
        headerBg: palette.bg.surface,
        borderRadiusLG: 14,
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
        titleColor: palette.text.primary,
      },

      Drawer: {
        colorBgElevated: palette.bg.surface,
      },

      Dropdown: {
        colorBgElevated: palette.bg.elevated,
        controlItemBgHover: palette.bg.hover,
        controlItemBgActive: palette.primarySubtle,
        borderRadiusLG: 10,
        boxShadowSecondary: '0 8px 24px rgba(0, 0, 0, 0.35)',
      },

      Tooltip: {
        colorBgSpotlight: palette.bg.elevated,
        colorTextLightSolid: palette.text.primary,
        borderRadius: 6,
      },

      Tabs: {
        inkBarColor: palette.primary,
        itemColor: palette.text.secondary,
        itemHoverColor: palette.text.primary,
        itemSelectedColor: palette.primary,
        itemActiveColor: palette.primary,
      },

      Segmented: {
        borderRadius: 8,
        itemSelectedBg: palette.primary,
        itemSelectedColor: '#ffffff',
        itemColor: palette.text.secondary,
        trackBg: palette.bg.hover,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: palette.text.tertiary,
        linkColor: palette.text.secondary,
        linkHoverColor: palette.text.primary,
        separatorColor: palette.text.tertiary,
      },

      Popover: {
        colorBgElevated: palette.bg.elevated,
      },

      Alert: {
        colorInfoBg: palette.infoSubtle,
        colorSuccessBg: palette.successSubtle,
        colorWarningBg: palette.warningSubtle,
        colorErrorBg: palette.errorSubtle,
      },

      Divider: {
        colorSplit: palette.border.subtle,
      },

      Skeleton: {
        color: palette.bg.hover,
        colorGradientEnd: palette.bg.active,
      },

      Empty: {
        colorTextDescription: palette.text.tertiary,
      },

      Spin: {
        colorPrimary: palette.primary,
      },

      Progress: {
        remainingColor: palette.bg.hover,
      },

      Switch: {
        colorPrimary: palette.primary,
        colorPrimaryHover: palette.primaryHover,
      },

      DatePicker: {
        colorBgContainer: palette.bg.elevated,
        colorBgElevated: palette.bg.elevated,
        activeBorderColor: palette.primary,
        hoverBorderColor: palette.border.strong,
      },

      Pagination: {
        itemBg: 'transparent',
        itemActiveBg: palette.primarySubtle,
      },

      Collapse: {
        headerBg: palette.bg.hover,
        contentBg: palette.bg.surface,
      },

      Timeline: {
        dotBg: palette.bg.surface,
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': palette.bg.surface,
    '--layout-sidebar-bg': palette.bg.base,
    '--layout-content-bg': palette.bg.base,

    '--surface-0': palette.bg.base,
    '--surface-1': palette.bg.surface,
    '--surface-2': palette.bg.elevated,
    '--surface-3': palette.bg.hover,

    '--text-primary': palette.text.primary,
    '--text-secondary': palette.text.secondary,
    '--text-tertiary': palette.text.tertiary,
    '--text-muted': palette.text.tertiary,

    '--border-default': palette.border.default,
    '--border-subtle': palette.border.subtle,
    '--border-strong': palette.border.strong,

    '--border-color': palette.border.default,
    '--border-radius': '8px',

    '--shadow-sm': '0 2px 4px rgba(0, 0, 0, 0.2)',
    '--shadow-md': '0 4px 16px rgba(0, 0, 0, 0.25)',
    '--shadow-lg': '0 16px 48px rgba(0, 0, 0, 0.4)',

    '--accent-warm': palette.warning,
    '--accent-cool': palette.info,

    '--status-success': palette.success,
    '--status-warning': palette.warning,
    '--status-error': palette.error,
    '--status-info': palette.info,

    '--status-success-subtle': palette.successSubtle,
    '--status-warning-subtle': palette.warningSubtle,
    '--status-error-subtle': palette.errorSubtle,
    '--status-info-subtle': palette.infoSubtle,

    '--control-bg': palette.bg.elevated,
    '--control-border': palette.border.default,
    '--control-hover': palette.bg.hover,

    '--hover-bg': palette.bg.hover,
    '--active-bg': palette.bg.active,
  },
};
